import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Trash, Plus, RotateCcw } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./components/ui/form";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import React, { useMemo, useState } from "react";

import { type OnChangeFn, type RowSelectionState } from "@tanstack/react-table";

import { DialogDrawer } from "./components/DialogDrawer";
import { EditableDataTable } from "./components/EditableDataTable";
import { Checkbox } from "./components/ui/checkbox";
import {
  defaultIncomeData,
  FrequencyTypeArr,
  IncomeDataSchema,
  initialIncomeData,
} from "./data";
import type {
  CreateHandlerFlat,
  DeleteHandlerFlat,
  UpdateHandlerFlat,
} from "./hooks/useCrudReducer";
import { calculateTotalIncomePerYear } from "./lib/calculators";
import {
  useEditableTable,
  type EditableColumnDef,
} from "./hooks/useEditableTable";
import { ResetButton } from "./components/ResetButton";
import { SubSectionCard } from "./components/SubSectionCard";

const incomeDatacolumns: EditableColumnDef<IncomeDataSchema>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    meta: {
      editable: false,
    },
  },
  {
    accessorKey: "amount",
    header: "Amount ($)",
    meta: {
      editable: true,
    },
  },
  {
    accessorKey: "frequency",
    header: "Frequency",
    meta: {
      editable: true,
    },
  },
  {
    accessorKey: "description",
    header: "Description",
    meta: {
      editable: true,
    },
  },
  {
    id: "actions",
    cell: ({ table, row }) => (
      <Button
        variant="ghost"
        className="h-8 w-8 p-0"
        onClick={() => table.options.meta!.deleteRow(row)}
      >
        <span className="sr-only">Remove</span>
        <Trash className="h-4 w-4" />
      </Button>
    ),
    meta: {
      editable: false,
    },
  },
];

interface IncomeFormProps {
  initialValues: IncomeDataSchema;
  onFormSubmit: (values: IncomeDataSchema) => void;
  onFormCancel: () => void;
}

function IncomeForm({
  initialValues,
  onFormSubmit,
  onFormCancel,
}: IncomeFormProps) {
  const form = useForm<IncomeDataSchema>({
    resolver: zodResolver(IncomeDataSchema),
    values: initialValues,
  });

  function onSubmitHandler(values: IncomeDataSchema) {
    onFormSubmit(values);
  }

  function onCancelHandler() {
    form.reset();
    onFormCancel();
  }

  const numToInputString = (num: number) => (num === 0 ? "" : num.toString());

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmitHandler)} className="space-y-8">
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Amount</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...field}
                  value={numToInputString(field.value)}
                  onChange={(e) => field.onChange(+e.target.value)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="frequency"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Frequency</FormLabel>
              <FormControl>
                <Select {...field} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {FrequencyTypeArr.map((val) => (
                      <SelectItem value={val}>{val}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Input type="text" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="w-full mb-2">
          Save
        </Button>
        <Button
          type="button"
          className="w-full"
          variant="secondary"
          onClick={onCancelHandler}
        >
          Cancel
        </Button>
      </form>
    </Form>
  );
}

interface DialogDrawerIncomeFormProps extends React.ComponentProps<"div"> {
  data: IncomeDataSchema;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFormSubmit: (data: IncomeDataSchema) => void;
}

function DialogDrawerIncomeForm({
  data,
  onFormSubmit,
  open,
  onOpenChange,
}: DialogDrawerIncomeFormProps) {
  const handleFormSubmit = (data: IncomeDataSchema) => {
    onFormSubmit(data);
    onOpenChange(false);
  };

  return (
    <DialogDrawer
      title="Edit expense"
      description="Make changes to your expense here. Click save when you're done."
      onOpenChange={onOpenChange}
      open={open}
    >
      <IncomeForm
        initialValues={data}
        onFormSubmit={handleFormSubmit}
        onFormCancel={() => onOpenChange(false)}
      />
    </DialogDrawer>
  );
}

interface AddButtonProps {
  onClick: () => void;
}

function DataTableAddButton({ onClick }: AddButtonProps) {
  return (
    <Button
      variant="ghost"
      className="h-8 w-full p-0"
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      <span className="sr-only">Add</span>
      <Plus className="h-4 w-4" />
    </Button>
  );
}

interface IncomeSectionProps {
  incomeData: IncomeDataSchema[];
  onCreate: CreateHandlerFlat<IncomeDataSchema>;
  onUpdate: UpdateHandlerFlat<IncomeDataSchema>;
  onDelete: DeleteHandlerFlat<IncomeDataSchema>;
}

export function IncomeSection({
  incomeData,
  onCreate,
  onUpdate,
  onDelete,
}: IncomeSectionProps) {
  const totalIncomePerYear = calculateTotalIncomePerYear(incomeData);

  const [selectedRow, setSelectedRow] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  const rowSelection = useMemo(
    () =>
      incomeData.reduce((acc, entry, idx) => {
        return {
          ...acc,
          [idx]: entry.selected,
        };
      }, {} as RowSelectionState),
    [incomeData]
  );
  const onRowSelectionChange: OnChangeFn<RowSelectionState> = (
    rowSelectionUpdater
  ) => {
    const newRowSelection =
      typeof rowSelectionUpdater === "function"
        ? rowSelectionUpdater(rowSelection)
        : rowSelectionUpdater;
    const newData = incomeData.map((entry, idx) => ({
      ...entry,
      selected: newRowSelection[idx] || false,
    }));
    onUpdate(newData);
  };

  const table = useEditableTable({
    columns: incomeDatacolumns,
    data: incomeData,
    onCreate: () => onCreate(defaultIncomeData()),
    onDelete: (row) => {
      const entry = incomeData[row.index];
      if (entry) {
        onDelete(entry);
      }
    },
    rowSelection,
    onRowSelectionChange,
  });

  return (
    <div>
      <DialogDrawerIncomeForm
        onFormSubmit={(newData) => onUpdate(newData)}
        data={incomeData[selectedRow] || defaultIncomeData()}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
      <SubSectionCard
        title={
          <div className="flex flex-row">
            <div className="grow">Income</div>
            <ResetButton
              onClick={() => {
                onDelete(incomeData);
                onCreate(initialIncomeData);
              }}
            />
          </div>
        }
        description="Use post-tax earnings for accurate money power"
      >
        <EditableDataTable
          table={table}
          onCellClick={(cell) => {
            setSelectedRow(cell.row.index);
            setDialogOpen(true);
          }}
        />
        <DataTableAddButton
          onClick={() => {
            onCreate(defaultIncomeData());
          }}
        />
        <div className="flex-col items-start gap-1 text-md">
          <div className="text-muted-foreground">
            ${+(totalIncomePerYear / 52).toFixed(2)} per week
          </div>
          <div className="text-muted-foreground">
            ${+(totalIncomePerYear / 26).toFixed(2)} per fortnight
          </div>
          <div className="text-muted-foreground">
            ${+(totalIncomePerYear / 12).toFixed(2)} per month
          </div>
        </div>
      </SubSectionCard>
    </div>
  );
}
